import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import {
	Alert,
	Backdrop,
	CircularProgress,
	Grid,
	IconButton,
	InputAdornment,
	MenuItem,
	Pagination,
	Paper,
	Select,
	SelectChangeEvent,
	Snackbar,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	TextField,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import { pageSizeOptions } from 'constants/constants';

import { extractErrorMessage } from 'features/quotation/helpers';
import { FilterGrid } from 'features/common/components/FilterGrid/FilterGrid';
import { PageNoResult } from 'features/common/components/PageNoResults';
import { ExportToExcelButton } from 'features/common/components/ExportToExcelButton';
import {
	convertFiltersToRecord,
	createSortHandler,
	ExportExcelOptions,
	exportToExcel,
	getSortableFields,
} from 'features/common/helpers';
import { FilterModel, ISort, ShowAlertState } from 'features/common/types';

import { getFlightAndAncillaryETicketsList, getFlightAndAncillaryETicketsListToExport } from '../../services';
import { IFlightAndFlightAncillaryETicketSummary } from '../../types';
import { flightAndAncillaryETicketSummaryListStyles, stickyWidths } from './styles';
import routes from 'constants/routes';
import DealOrCaseOpenDetail from '../../../common/components/DealOrCaseOpenDetail';
import {
	flightAndAncillaryETicketSummaryListFilters,
	flightAndAncillaryETicketSummaryListHeaderCells,
	transformValue,
} from './constants';

export const FlightAndAncillaryETicketSummaryList = (): JSX.Element => {
	const classes = flightAndAncillaryETicketSummaryListStyles();
	const [loading, setLoading] = useState<boolean>(false);
	const defaultAlertState: ShowAlertState = { show: false, severity: 'success', message: '' };
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [sortFields, setSortFields] = useState<ISort[]>([{ field: 'serviceCreatedAt', order: 'desc' }]);
	const [currentSize, setCurrentSize] = useState<number>(10);
	const [pageTotal, setPageTotal] = useState<number>(0);
	const [totalCount, setTotalCount] = useState<number>(0);
	const [filterApplied, setFilterApplied] = useState<FilterModel[]>([]);
	const [summaryList, setSummaryList] = useState<IFlightAndFlightAncillaryETicketSummary[] | null>([]);
	const colSpan = flightAndAncillaryETicketSummaryListHeaderCells.length;
	const [searchValue, setSearchValue] = useState<string>('');

	const handleApplyFilters = (filters: FilterModel[]) => {
		setFilterApplied(filters);
	};

	useEffect(() => {
		loadSummaryList();
	}, [filterApplied, sortFields, currentPage, currentSize]);

	const loadSummaryList = useCallback(async () => {
		try {
			setLoading(true);
			const results = await getFlightAndAncillaryETicketsList(
				currentPage,
				currentSize,
				sortFields,
				convertFiltersToRecord(filterApplied),
				searchValue,
			);
			setSummaryList(results.data.elements);
			setPageTotal(Math.ceil(results.data.total / currentSize));
			setTotalCount(results.data.total);
			setLoading(false);
		} catch (error) {
			console.error('Failed to load data:', error);
			setAlert({ show: true, severity: 'error', message: 'Error al cargar los datos.' });
			setLoading(false);
		}
	}, [currentPage, currentSize, sortFields, filterApplied, searchValue]);

	const handleOpenSaleOrderDetail = (id: string, serviceId: string) => () => {
		let newUrl = routes.salesOrder.edit.replace(':id', id);
		newUrl = newUrl + `?serviceId=${serviceId}`;
		window.open(newUrl, '_blank');
	};

	const sortableFields = getSortableFields(flightAndAncillaryETicketSummaryListHeaderCells);

	const handleExportToExcel = async () => {
		try {
			setLoading(true);
			const title = 'Listado de etickets de vuelos y ancillary';
			const envelopedTransactionList = (
				await getFlightAndAncillaryETicketsListToExport(sortFields, convertFiltersToRecord(filterApplied ?? []))
			).data;
			const listToExport = envelopedTransactionList.elements;
			const optionsToExport = {
				title: title,
				headers: flightAndAncillaryETicketSummaryListHeaderCells,
				widthColumns: [20, 20, 30, 20, 15, 20, 35, 35, 20, 20, 30, 20, 20, 20, 20],
				filename: title,
			} as ExportExcelOptions;
			exportToExcel(listToExport, optionsToExport, transformValue);
			setLoading(false);
		} catch (error) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al exportar el listado'),
			});
			setLoading(false);
		}
	};

	const handleSearch = () => {
		setCurrentPage(1);
		loadSummaryList();
	};

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchValue(event.target.value);
	};

	const handleCloseAlert = () => {
		setAlert(defaultAlertState);
	};

	const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
		setCurrentSize(+event.target.value);
		setCurrentPage(1);
	};
	const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
		setCurrentPage(page);
	};

	useEffect(() => {
		loadSummaryList();
	}, [filterApplied, sortFields, currentPage, currentSize, loadSummaryList]);

	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
	const getStickyClass = (index: number) => {
		return !isSmallScreen && index < stickyWidths.length
			? (classes as Record<string, string>)[`sticky${index + 1}`] || ''
			: '';
	};

	return (
		<>
			<Backdrop className={classes.backdrop} open={loading}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<Grid
				container
				spacing={1}
				sx={{
					justifyContent: 'center',
				}}
			>
				<Grid container spacing={2} sx={{ maxWidth: '90vw' }} justifyContent="flex-end" alignItems="center">
					<Grid item xs={12} md="auto" className={classes.containerButtons} gap={1}>
						<Tooltip title="Buscá por Ticket, Item de la orden, SO, Nombre del pasajero, PNR o Caso de venta">
							<TextField
								label="Buscar"
								variant="outlined"
								size="small"
								value={searchValue}
								onChange={handleChange}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton onClick={handleSearch}>
												<SearchIcon />
											</IconButton>
										</InputAdornment>
									),
								}}
							/>
						</Tooltip>
					</Grid>
					<Grid item xs={12} md="auto">
						<FilterGrid
							filters={flightAndAncillaryETicketSummaryListFilters}
							preAppliedFilters={filterApplied}
							handleAppliedFilters={handleApplyFilters}
						/>
					</Grid>

					<Grid item xs={12} md="auto" sx={{ pl: 2 }}>
						<ExportToExcelButton disabled={summaryList?.length == 0} handleExportToExcel={handleExportToExcel} />
					</Grid>
				</Grid>
				<Grid item xs={12} sx={{ m: 5, justifyContent: 'flex-start', ml: 19 }}>
					<Typography variant="subtitle1">
						En pantalla: {summaryList?.length} resultados de {totalCount}
					</Typography>
				</Grid>
				<TableContainer component={Paper} sx={{ maxWidth: '90vw' }} className={classes.tableContainer}>
					<Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
						<TableHead>
							<TableRow>
								{flightAndAncillaryETicketSummaryListHeaderCells.map((cell, index) => {
									const stickyClass = getStickyClass(index);
									const sortDirection = sortFields.find((x) => x.field === cell.field)
										? sortFields.find((x) => x.field === cell.field)?.order
										: false;

									return (
										<TableCell
											className={`${classes.cellNoWrap} ${stickyClass}`}
											key={cell.field}
											align="center"
											sortDirection={sortDirection}
										>
											{cell.sortable ? (
												<TableSortLabel
													active={sortFields.some((x) => x.field === cell.field)}
													direction={sortDirection || 'asc'}
													onClick={createSortHandler(cell.field, sortFields, sortableFields, setSortFields)}
												>
													{cell.headerName}
												</TableSortLabel>
											) : (
												cell.headerName
											)}
										</TableCell>
									);
								})}
							</TableRow>
						</TableHead>
						<TableBody>
							{summaryList?.length === 0 ? (
								<TableRow>
									<TableCell colSpan={colSpan}>
										<PageNoResult />
									</TableCell>
								</TableRow>
							) : (
								summaryList?.map((item) => (
									<React.Fragment key={item.keyId}>
										<TableRow
											sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
											onDoubleClick={handleOpenSaleOrderDetail(item.saleOrderId, item.serviceId)}
											className={classes.hoverRow}
										>
											<TableCell
												align="center"
												className={`${classes.cellNoWrap}  ${classes.clickableCell} ${getStickyClass(0)}`}
												onClick={handleOpenSaleOrderDetail(item.saleOrderId, item.serviceId)}
											>
												<Tooltip title="Hacé clic para ver detalles en una nueva pestaña">
													<div>{item.eTicket} </div>
												</Tooltip>
											</TableCell>

											<TableCell align="center" className={`${classes.cellNoWrap} ${getStickyClass(1)}`}>
												{transformValue('serviceCreatedAt', item.serviceCreatedAt, item)}
											</TableCell>

											<TableCell align="center" className={`${classes.cellNoWrap} ${getStickyClass(2)}`}>
												{item.passengerName}
											</TableCell>

											<TableCell align="center" className={`${classes.cellNoWrap} ${getStickyClass(3)}`}>
												{transformValue('birthdate', item.birthdate, item)}
											</TableCell>

											<TableCell align="center" className={`${classes.cellNoWrap} ${getStickyClass(4)}`}>
												{item.serviceReference}
											</TableCell>

											<TableCell align="center" className={`${classes.cellNoWrap} ${getStickyClass(5)} `}>
												<DealOrCaseOpenDetail dealId={item.saleOrderDealId} isBitrixDeal={item.saleOrderIsBitrixDeal} />
											</TableCell>

											<TableCell
												align="center"
												className={`${classes.cellNoWrap} ${classes.clickableCell}`}
												onClick={handleOpenSaleOrderDetail(item.saleOrderId, item.serviceId)}
											>
												<Tooltip title="Hacé clic para ver detalles en una nueva pestaña">
													<div>{item.saleOrderId} </div>
												</Tooltip>
											</TableCell>

											<TableCell
												align="center"
												className={`${classes.cellNoWrap} ${classes.clickableCell}`}
												onClick={handleOpenSaleOrderDetail(item.saleOrderId, item.serviceId)}
											>
												<Tooltip title="Hacé clic para ver detalles en una nueva pestaña">
													<div>{item.serviceId} </div>
												</Tooltip>
											</TableCell>

											<TableCell align="center" className={classes.cellNoWrap}>
												{item.issueRequestCaseId ? (
													<DealOrCaseOpenDetail dealId={item.issueRequestCaseId} isBitrixDeal={false} />
												) : (
													'-'
												)}
											</TableCell>

											<TableCell align="center" className={classes.cellNoWrap}>
												{transformValue('issueRequestCaseCreatedAt', item.issueRequestCaseCreatedAt, item)}
											</TableCell>

											<TableCell align="center" className={classes.cellNoWrap}>
												{transformValue('providerFantasyName', item.providerFantasyName, item)}
											</TableCell>

											<TableCell align="center" className={classes.cellNoWrap}>
												{transformValue('serviceCost', item.serviceCost, item)}
											</TableCell>

											<TableCell align="center" className={classes.cellNoWrap}>
												{transformValue('paymentDateToProvider', item.paymentDateToProvider, item)}
											</TableCell>

											<TableCell align="center" className={classes.cellNoWrap}>
												{item.vcCode}
											</TableCell>

											<TableCell align="center" className={classes.cellNoWrap}>
												{transformValue('ticketType', item.ticketType, item)}
											</TableCell>
										</TableRow>
									</React.Fragment>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<Grid item xs={12}>
					<Grid container justifyContent="center" flexDirection="row" padding={2}>
						<Grid item xs={12} md={1} textAlign="center">
							<Select value={currentSize} onChange={handleRowsPerPageChange}>
								{pageSizeOptions.map((value) => (
									<MenuItem key={value} value={value}>
										{value}
									</MenuItem>
								))}
							</Select>
						</Grid>
						<Grid item xs={12} md={6} marginTop={1}>
							<Pagination count={pageTotal} page={currentPage} onChange={handlePageChange} color="primary" />
						</Grid>
					</Grid>
				</Grid>
			</Grid>
			<Snackbar open={alert.show} autoHideDuration={2000} onClose={handleCloseAlert}>
				<Alert variant="filled" severity={alert.severity}>
					{alert.message}
				</Alert>
			</Snackbar>
		</>
	);
};
